import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';

import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@src/api/actions/dashboard/notifications';
import { RouterLink } from '@src/components/core/link';
import Page from '@src/components/layout/Page';
import paths from '@src/paths';

function formatDateTime(value) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function NotificationsPage() {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [count, setCount] = useState(0);

  const loadNotifications = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const response = await fetchNotifications({ page: nextPage, page_size: 20 });
      const payload = response?.data || {};
      const rows = Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload)
          ? payload
          : [];
      setNotifications(rows);
      setCount(Number(payload?.count || rows.length || 0));
      const totalPages = Number(payload?.count)
        ? Math.max(1, Math.ceil(Number(payload.count) / 20))
        : 1;
      setPageCount(totalPages);
      setPage(nextPage);
    } catch {
      setNotifications([]);
      setCount(0);
      setPageCount(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications(1);
  }, [loadNotifications]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          is_read: true,
          read_at: item.read_at || new Date().toISOString(),
        }))
      );
    } catch {
      // Ignore soft failures on the listing page.
    }
  }, []);

  const handleMarkRead = useCallback(async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }
            : item
        )
      );
    } catch {
      // Ignore soft failures on the listing page.
    }
  }, []);

  return (
    <Page title="Notifications">
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4">Notifications</Typography>
            <Typography color="text.secondary" variant="body2">
              Recent client and system updates that may need your attention.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button disabled={!notifications.length} onClick={handleMarkAllRead} variant="outlined">
              Mark All Read
            </Button>
            <Button component={RouterLink} href={paths.budgetCenter} variant="text">
              Open Budget Center
            </Button>
          </Stack>
        </Stack>

        <Typography color="text.secondary" variant="body2">
          {count} notification{count === 1 ? '' : 's'}
        </Typography>

        {loading ? (
          <Stack alignItems="center" py={8}>
            <CircularProgress />
          </Stack>
        ) : notifications.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6">No notifications</Typography>
              <Typography color="text.secondary" variant="body2">
                New alerts will appear here when clients save budgets, approve budgets, or other tracked events occur.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {notifications.map((notification) => (
              <Card
                key={notification.id}
                sx={{
                  borderRadius: 3,
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: notification.is_read ? 'divider' : 'primary.main',
                }}
                variant="outlined"
              >
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="h6">{notification.title}</Typography>
                        {!notification.is_read ? <Chip color="primary" label="New" size="small" variant="soft" /> : null}
                        {notification.client?.name ? (
                          <Chip label={notification.client.name} size="small" variant="outlined" />
                        ) : null}
                      </Stack>
                      <Typography color="text.secondary" variant="body2">
                        {formatDateTime(notification.created_at)}
                      </Typography>
                    </Stack>

                    {notification.body ? (
                      <Typography color="text.secondary" variant="body2">
                        {notification.body}
                      </Typography>
                    ) : null}

                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Typography color="text.secondary" variant="caption">
                        {notification.actor?.name || notification.actor?.email || 'System'}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {!notification.is_read ? (
                          <Button onClick={() => handleMarkRead(notification.id)} size="small" variant="text">
                            Mark Read
                          </Button>
                        ) : null}
                        {notification.link ? (
                          <Button component={RouterLink} href={notification.link} size="small" variant="contained">
                            Open
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {pageCount > 1 ? (
          <Stack alignItems="center">
            <Pagination count={pageCount} page={page} onChange={(_, value) => loadNotifications(value)} />
          </Stack>
        ) : null}
      </Stack>
    </Page>
  );
}
